import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Form,
  Col,
  Row,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";

const DeliveryNoteTable = ({ canUpdate, progressData, loading}) => {

  const [data, setData] = useState([]);
  const [form] = Form.useForm();
  const [priorities, setPriorities] = useState([]);
  const [uom, setUom] = useState("");
  

  useEffect(() => {
    if (progressData) {
      const extractedPriorities = extractPriorities(progressData);
      setPriorities(extractedPriorities);
      
      // Extract UOM from the first material type
      const firstMaterialType = Object.keys(progressData)[0];
      if (firstMaterialType && progressData[firstMaterialType]) {
        setUom((progressData[firstMaterialType].uom || "").toLowerCase());
      }
      
      const formattedData = formatProgressData(progressData);
      setData(formattedData);
      
    }
  }, [progressData]);
  
  // Helper function to compare priorities for sorting
  const comparePriorities = (a, b) => {
    // Extract numeric part if priority starts with P followed by a number
    const getNumericPriority = (priority) => {
      const match = priority.match(/^P(\d+)$/i);
      return match ? parseInt(match[1], 10) : null;
    };
    
    const numA = getNumericPriority(a);
    const numB = getNumericPriority(b);
    
    // If both are P-format priorities (P1, P2, etc.), compare numerically
    if (numA !== null && numB !== null) {
      return numA - numB;
    }
    
    // If only one is P-format, prioritize it
    if (numA !== null) return -1;
    if (numB !== null) return 1;
    
    // For priorities like "Priority 100", extract the number
    const getPriorityNumber = (priority) => {
      const match = priority.match(/Priority\s+(\d+)/i);
      return match ? parseInt(match[1], 10) : null;
    };
    
    const prioNumA = getPriorityNumber(a);
    const prioNumB = getPriorityNumber(b);
    
    // If both are "Priority X" format, compare numerically
    if (prioNumA !== null && prioNumB !== null) {
      return prioNumA - prioNumB;
    }
    
    // If only one is "Priority X" format, prioritize it
    if (prioNumA !== null) return -1;
    if (prioNumB !== null) return 1;
    
    // Default to alphabetical comparison
    return a.localeCompare(b);
  };
  
  // Extract material type + priority combinations for rendering headers above the table
  const extractPriorities = (data) => {
    const materialPrioritiesMap = {};
    const materialTypesWithUom = {};
    const materialItemsWithModels = {};
    
    // First pass: collect all material types with their UOMs and items with models
    Object.keys(data).forEach(materialType => {
      const materialData = data[materialType];
      // Store the UOM for each material type
      if (materialData.uom) {
        materialTypesWithUom[materialType] = materialData.uom;
      }
      
      // Store items with their model numbers
      if (materialData.items) {
        materialItemsWithModels[materialType] = {};
        Object.keys(materialData.items).forEach(itemId => {
          const item = materialData.items[itemId];
          if (item && item.model) {
            materialItemsWithModels[materialType][itemId] = {
              id: item.id,
              model: item.model,
              markNo: item.markNo,
              description: item.description,
              qty: item.qty,
              totalGross: item.totalGross
            };
          }
        });
      }
    });
    
    // Second pass: create priority entries with UOM information
    Object.keys(data).forEach(materialType => {
      const materialData = data[materialType];
      
      // Skip the 'uom' and 'items' properties as they're not priorities
      const priorityKeys = Object.keys(materialData).filter(key => key !== 'uom' && key !== 'items');
      
      // Sort priorities for this material type
      priorityKeys.sort(comparePriorities);
      
      // Get all priorities for this material type
      priorityKeys.forEach(priority => {
        const combinedKey = `${materialType.toUpperCase()} ${priority.toUpperCase()}`;
        materialPrioritiesMap[combinedKey] = { 
          materialType, 
          priority,
          uom: materialTypesWithUom[materialType] || '',
          items: materialItemsWithModels[materialType] || {}
        };
      });
    });
    
    // If there are no priorities but there are items, create an entry for the material type
    Object.keys(data).forEach(materialType => {
      const materialData = data[materialType];
      const priorityKeys = Object.keys(materialData).filter(key => key !== 'uom' && key !== 'items');
      
      if (priorityKeys.length === 0 && materialData.items && Object.keys(materialData.items).length > 0) {
        const combinedKey = `${materialType.toUpperCase()}`;
        materialPrioritiesMap[combinedKey] = { 
          materialType, 
          priority: '',
          uom: materialTypesWithUom[materialType] || '',
          items: materialItemsWithModels[materialType] || {}
        };
      }
    });
    
    // Convert to array and sort by material type first, then by priority
    return Object.keys(materialPrioritiesMap).sort((a, b) => {
      const itemA = materialPrioritiesMap[a];
      const itemB = materialPrioritiesMap[b];
      
      // First sort by material type
      if (itemA.materialType !== itemB.materialType) {
        return itemA.materialType.localeCompare(itemB.materialType);
      }
      
      // Then sort by priority
      return comparePriorities(itemA.priority, itemB.priority);
    }).map(key => ({
      key,
      materialType: materialPrioritiesMap[key].materialType,
      priority: materialPrioritiesMap[key].priority,
      uom: materialPrioritiesMap[key].uom,
      items: materialPrioritiesMap[key].items
    }));
  };

  // Group data by material and priority
  const groupDataByMaterialAndPriority = (data) => {
    const groupedData = {};
    
    data.forEach(item => {
      const key = item.materialPriority;
      if (!groupedData[key]) {
        groupedData[key] = [];
      }
      groupedData[key].push(item);
    });
    
    return groupedData;
  };
  
  // Transform API data into table-compatible format for the nested priority and area structure
  const formatProgressData = (data) => {
    const formattedData = [];
    let position = 0;
    
    // Iterate through each material type (Bolts, Area, Len, Hello, etc.)
    Object.keys(data).forEach(materialType => {
      const materialData = data[materialType];
      
      // Skip the 'uom' and 'items' properties
      const priorityKeys = Object.keys(materialData).filter(key => key !== 'uom' && key !== 'items');
      
      // If there are no priorities but there are items, create rows directly from items
      if (priorityKeys.length === 0 && materialData.items && Object.keys(materialData.items).length > 0) {
        Object.keys(materialData.items).forEach(itemId => {
          const item = materialData.items[itemId];
          formattedData.push({
            id: position,
            position: position++,
            materialType: materialType,
            priority: '',
            materialPriority: `${materialType.toUpperCase()}`, // Combined key for grouping
            description: item.markNo || item.description || `Item ${itemId}`,
            model: item.model || '',
            qty: item.qty || 0,
            grossTotal: item.totalGross || 0,
            uom: materialData.uom || '',
            fabrication: { totalGross: 0, percentageCompleted: 0 },
            qcRelease: { totalGross: 0, percentageCompleted: 0 },
            corrisProtDelWrkshp: { totalGross: 0, percentageCompleted: 0 },
            delSite: { totalGross: 0, percentageCompleted: 0 },
            siteReceipt: { totalGross: 0, percentageCompleted: 0 }
          });
        });
        return;
      }
      
      // Iterate through each priority (P3, Priority 100, etc.)
      priorityKeys.forEach(priority => {
        const priorityData = materialData[priority];
        
        // Iterate through each area (area-two, Area, A, etc.)
        Object.keys(priorityData).forEach(area => {
          const areaData = priorityData[area];
          
          // Find matching item in items array if available
          let matchingItem = null;
          if (materialData.items) {
            matchingItem = Object.values(materialData.items).find(item => 
              item.markNo === area || item.description === area
            );
          }
          
          // Create a row for this area under the priority
          formattedData.push({
            id: position,
            position: position++,
            materialType: materialType,
            priority: priority,
            materialPriority: `${materialType.toUpperCase()} ${priority.toUpperCase()}`, // Combined key for grouping
            description: area, // Using the area name directly as the description
            model: matchingItem?.model || '',
            qty: matchingItem?.qty || 0,
            grossTotal: areaData['Total / Gross'] || matchingItem?.totalGross || 0,
            uom: materialData.uom || '',
          });
        });
      });
    });
    
    return formattedData;
  };

  const columns = [
    {
        title: "Item",
        dataIndex: "item",
        key: "item",
        width: "10%",
        editable: true,
        align: "left",
        render: (_, __, index) => index + 1,
      },
    {
      title: "Model No",
      dataIndex: "model",
      key: "model",
      width: "10%",
      editable: true,
      align: "left",
      render: (_, record) => {
        const item = findMatchingItem(record);
        return item?.model || '-';
      },
    },

    {
      title: "Draw / Mark No",
      dataIndex: "markNo",
      key: "markNo",
      width: "10%",
      editable: true,
      align: "left",
      render: (_, record) => {
        const item = findMatchingItem(record);
        return item?.markNo || record.description || '-';
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "20%",
      editable: true,
      align: "left",
      render: (description, record) => {
        const item = findMatchingItem(record);
        return item?.description || description || '-';
      },
    },
    {
      title: "Quantity",
      dataIndex: "qty",
      key: "qty",
      width: "10%",
      editable: true,
      align: "center",
      render: (qty, record) => {
        const item = findMatchingItem(record);
        return (item?.qty || qty || 0);
      },
    },
    {
      title: "Total / Gross",
      dataIndex: "grossTotal",
      key: "grossTotal",
      width: "10%",
      editable: true,
      align: "center",
      render: (grossTotal, record) => {
        const item = findMatchingItem(record);
        return (item?.totalGross || grossTotal || 0);
      },
    }
  ];
  
  // Helper function to find matching item for a record
  const findMatchingItem = (record) => {
    // Find the item in the priorities array
    const priorityItem = priorities.find(p => p.materialType === record.materialType);
    if (priorityItem && priorityItem.items) {
      // Find the item by description or markNo
      return Object.values(priorityItem.items).find(item => 
        item.description === record.description || 
        item.markNo === record.description
      );
    }
    return null;
  };

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: "text",
        dataIndex: col.dataIndex,
        // title: col.title,
        // editing: isEditing(record),
      }),
    };
  });

  // Render priority headers above the table
  const renderPriorityHeaders = () => {
    // Show all material-priority combinations
    return priorities.map((priorityItem, index) => {
      // Get items with model numbers for this material type
      const items = priorityItem.items || {};
      const hasItems = Object.keys(items).length > 0;
      
      return (
        <div key={`priority-header-${index}`}>
          <div 
            className="priority-header"
            style={{
              backgroundColor: '#f0f0f0',
              padding: '12px 16px',
              fontWeight: 'bold',
              marginBottom: hasItems ? '8px' : '12px',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            {priorityItem.materialType.toUpperCase()} ({priorityItem.uom})
          </div>
          
          {/* Display model numbers for each item */}
          {hasItems && (
            <div className="model-numbers-container" style={{ marginBottom: '12px', paddingLeft: '20px' }}>
              {Object.keys(items).map(itemId => {
                const item = items[itemId];
                return item.model ? (
                  <div key={`model-${itemId}`} className="model-number-item" style={{ 
                    marginBottom: '4px', 
                    fontSize: '13px',
                    display: 'flex'
                  }}>
                    <div style={{ fontWeight: '500', marginRight: '8px' }}>{item.markNo || `Item ${itemId}`}:</div>
                    <div>Model: {item.model}</div>
                  </div>
                ) : null;
              })}
            </div>
          )}
        </div>
      );
    });
  };
  
  return (
    <Row className="production-manager-parent-row-delivery-note">

      <Col lg={24} xl={24} xxl={24} md={24}>
        <Form
          form={form}
          component={false}
          className="table-style"
        >
          <style>
            {`
              .ant-input {
                padding-left: 12px !important;
              }
              .ant-select-selector {
                padding-left: 12px !important;
              }
              .ant-select-selection-item {
                padding-left: 0px !important;
              }
              /* Target the actual input element inside ant-input */
              input.ant-input {
                text-indent: 8px;
              }
              .special-table-delivery-note {
                width: 100% !important;
              }
              .special-table-delivery-note .ant-table {
                width: 100% !important;
              }
              .special-table-delivery-note .ant-table-container {
                width: 100% !important;
              }
              .special-table-delivery-note .ant-table-content {
                width: 100% !important;
              }
              .special-table-delivery-note table {
                width: 100% !important;
              }
              /* Override card container border radius */
              .production-manager-parent-row.card-container {
                border-radius: 0 !important;
              }
              /* Style for priority sections */
              .priority-header {
                margin-bottom: 8px;
                position: relative;
              }
              .priority-header:after {
                content: '';
                position: absolute;
                bottom: -4px;
                left: 0;
                width: 100%;
                height: 1px;
                
              }
              /* Remove black line below the last row */
              .details-table .ant-table-tbody tr:last-child td {
                border-bottom: none !important;
              }
              /* Style for the total row */
              .total-row td {
                font-weight: bold;
                border-top: 1px solid #000 !important;
                border-bottom: none !important;
              }
              /* Ensure summary row has no border-bottom */
              .ant-table-summary .total-row td {
                border-bottom: none !important;
              }
              /* Remove border-bottom from all summary row cells */
              .details-table .ant-table-summary > tr > td {
                border-bottom: none !important;
              }
              /* Set padding to zero for space columns */
              .special-table-delivery-note .ant-table-thead > tr > th:nth-child(5),
              .special-table-delivery-note .ant-table-thead > tr > th:nth-child(8),
              .special-table-delivery-note .ant-table-thead > tr > th:nth-child(11),
              .special-table-delivery-note .ant-table-thead > tr > th:nth-child(14),
              .special-table-delivery-note .ant-table-tbody > tr > td:nth-child(5),
              .special-table-delivery-note .ant-table-tbody > tr > td:nth-child(8),
              .special-table-delivery-note .ant-table-tbody > tr > td:nth-child(11),
              .special-table-delivery-note .ant-table-tbody > tr > td:nth-child(14),
              .special-table-delivery-note .ant-table-summary > tr > td:nth-child(5),
              .special-table-delivery-note .ant-table-summary > tr > td:nth-child(8),
              .special-table-delivery-note .ant-table-summary > tr > td:nth-child(11),
              .special-table-delivery-note .ant-table-summary > tr > td:nth-child(14) {
                padding: 0 !important;
                width: 0 !important;
                min-width: 0 !important;
                max-width: 0 !important;
              }
              
              /* Override the colored borders from ProgressSummaryReporting component */
              .details-table .ant-table-thead > tr > th:nth-child(6),
              .details-table .ant-table-thead > tr > th:nth-child(7),
              .details-table .ant-table-thead > tr > th:nth-child(9),
              .details-table .ant-table-thead > tr > th:nth-child(10),
              .details-table .ant-table-thead > tr > th:nth-child(12),
              .details-table .ant-table-thead > tr > th:nth-child(13),
              .details-table .ant-table-thead > tr > th:nth-child(15),
              .details-table .ant-table-thead > tr > th:nth-child(16) {
                border-top: none !important;
              }
              
              /* Removed colored top borders */
              .details-table .ant-table-thead > tr > th:nth-child(3),
              .details-table .ant-table-thead > tr > th:nth-child(4),
              .details-table .ant-table-thead > tr > th:nth-child(6),
              .details-table .ant-table-thead > tr > th:nth-child(7),
              .details-table .ant-table-thead > tr > th:nth-child(9),
              .details-table .ant-table-thead > tr > th:nth-child(10),
              .details-table .ant-table-thead > tr > th:nth-child(12),
              .details-table .ant-table-thead > tr > th:nth-child(13),
              .details-table .ant-table-thead > tr > th:nth-child(15),
              .details-table .ant-table-thead > tr > th:nth-child(16) {
                border-top: none !important;
              }
              
              /* Removed colored bottom borders from total rows */
              .details-table .ant-table-summary > tr > td:nth-child(6),
              .details-table .ant-table-summary > tr > td:nth-child(7),
              .details-table .ant-table-summary > tr > td:nth-child(9),
              .details-table .ant-table-summary > tr > td:nth-child(10),
              .details-table .ant-table-summary > tr > td:nth-child(12),
              .details-table .ant-table-summary > tr > td:nth-child(13),
              .details-table .ant-table-summary > tr > td:nth-child(15),
              .details-table .ant-table-summary > tr > td:nth-child(16) {
                border-bottom: none !important;
                border-top: 1px solid #000 !important;
              }
              
              /* Removed colored left borders */
              .details-table .ant-table-thead > tr > th:nth-child(15),
              .details-table .ant-table-tbody > tr > td:nth-child(15),
              .details-table .ant-table-summary > tr > td:nth-child(15),
              .details-table .ant-table-thead > tr > th:nth-child(12),
              .details-table .ant-table-tbody > tr > td:nth-child(12),
              .details-table .ant-table-summary > tr > td:nth-child(12),
              .details-table .ant-table-thead > tr > th:nth-child(9),
              .details-table .ant-table-tbody > tr > td:nth-child(9),
              .details-table .ant-table-summary > tr > td:nth-child(9),
              .details-table .ant-table-thead > tr > th:nth-child(3),
              .details-table .ant-table-tbody > tr > td:nth-child(3),
              .details-table .ant-table-summary > tr > td:nth-child(3),
              .details-table .ant-table-thead > tr > th:nth-child(6),
              .details-table .ant-table-tbody > tr > td:nth-child(6),
              .details-table .ant-table-summary > tr > td:nth-child(6) {
                border-left: none !important;
              }
              
              /* Add border radius to top-left corner of fabrication column */
              .details-table .ant-table-thead > tr > th:nth-child(3) {
                border-top-left-radius: 12px !important;
                overflow: visible !important;
              }
              
              /* Add border radius to top-left corner of QC Release column */
              .details-table .ant-table-thead > tr > th:nth-child(6) {
                border-top-left-radius: 12px !important;
                overflow: visible !important;
              }
              
              /* Add border radius to top-left corner of CORRIS PROT/ DEL. WRKSHP column */
              .details-table .ant-table-thead > tr > th:nth-child(9) {
                border-top-left-radius: 8px !important;
                overflow: visible !important;
              }
              
              /* Removed colored right borders */
              .details-table .ant-table-thead > tr > th:nth-child(16),
              .details-table .ant-table-tbody > tr > td:nth-child(16),
              .details-table .ant-table-summary > tr > td:nth-child(16),
              .details-table .ant-table-thead > tr > th:nth-child(4),
              .details-table .ant-table-tbody > tr > td:nth-child(4),
              .details-table .ant-table-summary > tr > td:nth-child(4),
              .details-table .ant-table-thead > tr > th:nth-child(7),
              .details-table .ant-table-tbody > tr > td:nth-child(7),
              .details-table .ant-table-summary > tr > td:nth-child(7),
              .details-table .ant-table-thead > tr > th:nth-child(10),
              .details-table .ant-table-tbody > tr > td:nth-child(10),
              .details-table .ant-table-summary > tr > td:nth-child(10),
              .details-table .ant-table-thead > tr > th:nth-child(13),
              .details-table .ant-table-tbody > tr > td:nth-child(13),
              .details-table .ant-table-summary > tr > td:nth-child(13) {
                border-right: none !important;
              }
              
              /* Add border radius to top-right corner of fabricationPercentage column */
              .details-table .ant-table-thead > tr > th:nth-child(4) {
                border-top-right-radius: 12px !important;
                overflow: visible !important;
              }
              
              /* Add border radius to top-right corner of qcReleasePercentage column */
              .details-table .ant-table-thead > tr > th:nth-child(7) {
                border-top-right-radius: 12px !important;
                overflow: visible !important;
              }
              
              /* Add border radius to top-right corner of corrisProtDelWrkshpPercentage column */
              .details-table .ant-table-thead > tr > th:nth-child(10) {
                border-top-right-radius: 8px !important;
                overflow: visible !important;
              }
              
              /* Add border radius to top-left corner of DEL SITE column */
              .details-table .ant-table-thead > tr > th:nth-child(12) {
                border-top-left-radius: 8px !important;
                overflow: visible !important;
              }
              
              /* Add border radius to top-right corner of delSitePercentage column */
              .details-table .ant-table-thead > tr > th:nth-child(13) {
                border-top-right-radius: 8px !important;
                overflow: visible !important;
              }
              
              /* Add border radius to top-left corner of SITE RECEIPT column */
              .details-table .ant-table-thead > tr > th:nth-child(15) {
                border-top-left-radius: 8px !important;
                overflow: visible !important;
              }
              
              /* Add border radius to top-right corner of siteReceiptPercentage column */
              .details-table .ant-table-thead > tr > th:nth-child(16) {
                border-top-right-radius: 8px !important;
                overflow: visible !important;
              }
              
              /* Remove bottom borders */
              .details-table .ant-table-tbody tr:last-child td:nth-child(3),
              .details-table .ant-table-tbody tr:last-child td:nth-child(4),
              .details-table .ant-table-tbody tr:last-child td:nth-child(6),
              .details-table .ant-table-tbody tr:last-child td:nth-child(7),
              .details-table .ant-table-tbody tr:last-child td:nth-child(9),
              .details-table .ant-table-tbody tr:last-child td:nth-child(10),
              .details-table .ant-table-tbody tr:last-child td:nth-child(12),
              .details-table .ant-table-tbody tr:last-child td:nth-child(13),
              .details-table .ant-table-tbody tr:last-child td:nth-child(15),
              .details-table .ant-table-tbody tr:last-child td:nth-child(16) {
                border-bottom: none !important;
              }
              .details-table .ant-table-thead > tr > th:nth-child(11),
              .details-table .ant-table-thead > tr > th:nth-child(14) {
                border-radius: 0 !important;
              }
              
              /* Kept border radius but removed colored borders */
              .details-table .ant-table-summary > tr > td:nth-child(3) {
                border-bottom-left-radius: 12px !important;
                overflow: visible !important;
                border-bottom: none !important;
              }
              
              .details-table .ant-table-summary > tr > td:nth-child(4) {
                border-bottom-right-radius: 12px !important;
                overflow: visible !important;
                border-bottom: none !important;
                font-weight: 600;
              }
              
              /* Border radius for QC Release columns in the summary row */
              .details-table .ant-table-summary > tr > td:nth-child(6) {
                border-bottom-left-radius: 12px !important;
                overflow: visible !important;
                border-bottom: none !important;
              }
              
              .details-table .ant-table-summary > tr > td:nth-child(7) {
                border-bottom-right-radius: 12px !important;
                overflow: visible !important;
                border-bottom: none !important;
              }
              
              /* Border radius for CORRIS PROT/ DEL. WRKSHP columns in the summary row */
              .details-table .ant-table-summary > tr > td:nth-child(9) {
                border-bottom-left-radius: 8px !important;
                overflow: visible !important;
                border-bottom: none !important;
              }
              
              .details-table .ant-table-summary > tr > td:nth-child(10) {
                border-bottom-right-radius: 8px !important;
                overflow: visible !important;
                border-bottom: none !important;
              }
              
              /* Border radius for DEL SITE columns in the summary row */
              .details-table .ant-table-summary > tr > td:nth-child(12) {
                border-bottom-left-radius: 8px !important;
                overflow: visible !important;
                border-bottom: none !important;
              }
              
              .details-table .ant-table-summary > tr > td:nth-child(13) {
                border-bottom-right-radius: 8px !important;
                overflow: visible !important;
                border-bottom: none !important;
              }
              
              /* Border radius for SITE RECEIPT columns in the summary row */
              .details-table .ant-table-summary > tr > td:nth-child(15) {
                border-bottom-left-radius: 8px !important;
                overflow: visible !important;
                border-bottom: none !important;
              }
              
              .details-table .ant-table-summary > tr > td:nth-child(16) {
                border-bottom-right-radius: 8px !important;
                overflow: visible !important;
                border-bottom: none !important;
              }
              
              /* Remove border radius */
              .details-table .ant-table-tbody tr:last-child td:nth-child(3),
              .details-table .ant-table-tbody tr:last-child td:nth-child(4),
              .details-table .ant-table-tbody tr:last-child td:nth-child(6),
              .details-table .ant-table-tbody tr:last-child td:nth-child(7),
              .details-table .ant-table-tbody tr:last-child td:nth-child(9),
              .details-table .ant-table-tbody tr:last-child td:nth-child(10),
              .details-table .ant-table-tbody tr:last-child td:nth-child(12),
              .details-table .ant-table-tbody tr:last-child td:nth-child(13),
              .details-table .ant-table-tbody tr:last-child td:nth-child(15),
              .details-table .ant-table-tbody tr:last-child td:nth-child(16) {
                border-radius: 0 !important;
              }
            `}
          </style>
          {/* Render a separate table for each material-priority combination */}
          {priorities.map((priorityItem, index) => {
            // Filter data for this specific priority
            const priorityData = data.filter(item => item.materialPriority === priorityItem.key);
            
            // Calculate the starting index for this section
            const previousSectionsData = priorities.slice(0, index).reduce((acc, prevPriorityItem) => {
              const prevData = data.filter(item => item.materialPriority === prevPriorityItem.key);
              return acc + prevData.length;
            }, 0);
            
            // Create columns with continuous numbering for this section
            const columnsWithContinuousNumbering = columns.map(col => {
              if (col.key === 'item') {
                return {
                  ...col,
                  render: (_, __, rowIndex) => previousSectionsData + rowIndex + 1,
                };
              }
              return col;
            });
            
            const mergedColumnsForSection = columnsWithContinuousNumbering.map((col) => {
              if (!col.editable) {
                return col;
              }
              return {
                ...col,
                onCell: (record) => ({
                  record,
                  inputType: "text",
                  dataIndex: col.dataIndex,
                }),
              };
            });
            
            return (
              <div key={`priority-section-${index}`} style={{ marginBottom: '30px' }}>
                <div 
                  className="priority-header"
                  style={{
                    backgroundColor: '#f0f0f0',
                    padding: '12px 16px',
                    fontWeight: 'bold',
                    borderRadius: '4px',
                    marginBottom: '12px',
                    fontSize: '14px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  {priorityItem.materialType.toUpperCase()} ({priorityItem.uom})
                </div>
                <Table
                  className="special-table-delivery-note activity-type-table project-statuses-table details-table"
                  rowClassName={() => 'editable-row'}
                  dataSource={priorityData}
                  loading={loading}
                  columns={mergedColumnsForSection}
                  pagination={false}
                  rowKey="position"
                  style={{ width: '100%' }}
                  summary={() => {
                    // Calculate totals for this priority section
                    const qtyTotal = priorityData.reduce((sum, record) => {
                      const item = findMatchingItem(record);
                      const qty = item?.qty || record.qty || 0;
                      return sum + Number(qty);
                    }, 0);
                    
                    const sumGrossTotal = priorityData.reduce((sum, record) => {
                      const item = findMatchingItem(record);
                      const grossTotal = item?.totalGross || record.grossTotal || 0;
                      return sum + Number(grossTotal);
                    }, 0);
                    
                    return (
                      <Table.Summary fixed>
                        <Table.Summary.Row className="total-row">
                          <Table.Summary.Cell index={0} align="left">
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="center">
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align="center">
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} align="left">
                          TOTALS
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={4} align="center">
                          {qtyTotal}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={5} align="center">
                          {sumGrossTotal}
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    );
                  }}
                />
              </div>
            );
          })}
        </Form>
      </Col>
    </Row>
  );
};

export default DeliveryNoteTable;

